import type { Zone } from '@seek/audience-zones';
import type { ChaliceStore } from '../reducer';
import type { PrivatePolicyBanner, ZoneFeatures } from 'src/config/types';
import type { Brand, CountrySiteName, Language } from '@seek/melways-sites';

export const SET_LANGUAGE = 'SET_LANGUAGE';
export const SET_ZONE = 'SET_ZONE';
export const SET_ZONE_FEATURES = 'SET_ZONE_FEATURES';
export const SET_SITE = 'SET_SITE';

export interface AppConfigState {
  brand: Brand;
  site: CountrySiteName;
  language: Language;
  zoneFeatures: ZoneFeatures;
  zone: Zone;
  timedBanners: {
    privatePolicyBanner: PrivatePolicyBanner;
  };
}

export const initialState: AppConfigState = {
  brand: 'seek',
  site: 'candidate-seek-au',
  language: 'en',
  zone: 'anz-1',
  zoneFeatures: {},
  timedBanners: { privatePolicyBanner: {} },
};

export type AppConfigAction =
  | SetLanguageAction
  | SetZoneAction
  | SetSiteAction
  | SetZoneFeaturesAction;

export interface SetLanguageAction {
  type: typeof SET_LANGUAGE;
  payload: {
    language: Language;
  };
}

export interface SetZoneAction {
  type: typeof SET_ZONE;
  payload: {
    zone: Zone;
  };
}

export interface SetSiteAction {
  type: typeof SET_SITE;
  payload: {
    site: CountrySiteName;
  };
}

export interface SetZoneFeaturesAction {
  type: typeof SET_ZONE_FEATURES;
  payload: {
    zoneFeatures: ZoneFeatures;
  };
}

export const setLanguage = (language: Language): SetLanguageAction => ({
  type: SET_LANGUAGE,
  payload: { language },
});

export const setZone = (zone: Zone): SetZoneAction => ({
  type: SET_ZONE,
  payload: { zone },
});

export const setSite = (site: CountrySiteName): SetSiteAction => ({
  type: SET_SITE,
  payload: { site },
});

export const setZoneFeatures = (
  zoneFeatures: ZoneFeatures,
): SetZoneFeaturesAction => ({
  type: SET_ZONE_FEATURES,
  payload: { zoneFeatures },
});

export default function reducer(
  state: AppConfigState = initialState,
  action: AppConfigAction,
): AppConfigState {
  switch (action.type) {
    case SET_LANGUAGE: {
      const { language } = action.payload;

      return {
        ...state,
        language,
      };
    }
    case SET_ZONE: {
      const { zone } = action.payload;

      return {
        ...state,
        zone,
      };
    }
    case SET_SITE: {
      const { site } = action.payload;

      return {
        ...state,
        site,
      };
    }
    case SET_ZONE_FEATURES: {
      const { zoneFeatures } = action.payload;

      return {
        ...state,
        zoneFeatures,
      };
    }
    default:
      return state;
  }
}

export const selectPrivacyPolicyBanner = (state: ChaliceStore) =>
  state.appConfig.timedBanners.privatePolicyBanner;
export const selectLanguage = (state: ChaliceStore) => state.appConfig.language;
export const selectZone = (state: ChaliceStore) => state.appConfig.zone;
export const selectSite = (state: ChaliceStore) => state.appConfig.site;
export const selectZoneFeatures = (state: ChaliceStore) =>
  state.appConfig.zoneFeatures;
